import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Box, Fade } from '@mui/material';
import { useEffect } from 'react';
import { IdleTimerProvider } from 'react-idle-timer';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FlipScreenProvider } from './FlipScreenProvider';
import { SecureDataProvider } from './SecureDataProvider';
import { Loader } from './components/Loader';
import { convertMinutesToMilliseconds } from './formattingUtils';
import { IDLE_TIME_MODAL_DURATION_IN_MINUTES } from './modals/IdleTimeModal';
import Modal from './modals/Modal';
import { NotFound } from './pages/404';
import { Unauthorized } from './pages/Unauthorized';
import Confirm from './pages/confirm';
import Main from './pages/main';
import Start from './pages/start';
import {
  useGetCurrentHostName,
  useGetMerchantEnvPublicKey,
  useGetMerchantId,
  useGetMerchantIdleTimeInMinutes,
} from './services/configManager';
import { FEEDBACK_TYPE, setFeedbackSession } from './services/feedbackManager';
import { useGetSdkDisplayed, useLogout } from './services/flowManager';
import {
  useResetCurrentModal,
  useSetCurrentModal,
} from './services/modalManager';
import {
  useGetShowError,
  useGetUserProfile,
  useUserProfile,
} from './services/userManager';
import { useInitFeatureFlags } from './services/featureFlagsManager';
import { environment } from './environment';

const MainProtected = withAuthenticationRequired(Main);
const ConfirmProtected = withAuthenticationRequired(Confirm);

const App = () => {
  const { isLoading, error, user, isAuthenticated } = useAuth0();
  const location = useLocation();
  const hostName = useGetCurrentHostName();
  const merchantId = useGetMerchantId();
  const merchantPublicKey = useGetMerchantEnvPublicKey();
  const userProfile = useGetUserProfile();
  const updateUserProfile = useUserProfile();
  const sdkDisplayed = useGetSdkDisplayed();
  const logout = useLogout();
  const setCurrentModal = useSetCurrentModal();
  const merchantIdleTimeInMinutes = useGetMerchantIdleTimeInMinutes();
  const resetCurrentModal = useResetCurrentModal();
  const showError = useGetShowError();
  const initFF = useInitFeatureFlags();
  const idleTimeModalMillisecond = convertMinutesToMilliseconds(
    IDLE_TIME_MODAL_DURATION_IN_MINUTES,
  );
  const idleTime =
    merchantIdleTimeInMinutes >= 30
      ? convertMinutesToMilliseconds(merchantIdleTimeInMinutes)
      : convertMinutesToMilliseconds(30);
  const userAppUrl = user
    ? user[`${environment.auth0ScopeNamespace}/appUrl`]
    : '';

  const idleTimeHandler = () => {
    if (isAuthenticated) {
      setFeedbackSession(FEEDBACK_TYPE.LOGGED_OUT);
      logout();
    }
  };

  const idleTimePromptHandler = () => {
    if (!sdkDisplayed && isAuthenticated) {
      setCurrentModal('idleTime');
    }
  };

  useEffect(() => {
    if (merchantId && merchantPublicKey) {
      initFF(merchantId, merchantPublicKey);
    }
  }, [merchantId, merchantPublicKey]);

  const routes = [
    { path: '/', component: <Start /> },
    { path: '/index.html', component: <Navigate replace to="/" /> },
    { path: '/main', component: <MainProtected /> },
    { path: '/confirm', component: <ConfirmProtected /> },
    { path: '/*', component: <NotFound /> },
  ];

  if (showError) {
    throw new Error('Profile request failed.');
  }

  useEffect(() => {
    if (user && !userProfile) updateUserProfile();
  }, [user]);

  if (
    (!user || (userProfile && !userProfile.impersonateEnabled)) &&
    (error || (user && !hostName.includes(userAppUrl)))
  ) {
    return <Unauthorized />;
  }

  if (isLoading || (!!user && !userProfile))
    return (
      <Box height="100vh">
        <Loader />
      </Box>
    );
  return (
    <SecureDataProvider
      config={{
        ...(userProfile?.impersonateEnabled && { merchantId }),
      }}
    >
      <IdleTimerProvider
        timeout={idleTime}
        promptBeforeIdle={idleTimeModalMillisecond}
        onPrompt={idleTimePromptHandler}
        onIdle={idleTimeHandler}
        onActive={resetCurrentModal}
        throttle={500}
      >
        <Modal />
        <FlipScreenProvider>
          <Routes location={location}>
            {routes.map((route) => {
              return (
                <Route
                  key={route.path}
                  element={
                    <Fade in timeout={1000}>
                      <div>{route.component}</div>
                    </Fade>
                  }
                  path={route.path}
                />
              );
            })}
          </Routes>
        </FlipScreenProvider>
      </IdleTimerProvider>
    </SecureDataProvider>
  );
};

export default App;
